import React, { useState, useEffect, useCallback } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { API_BASE_URL } from '../config';
import {
  People as PeopleIcon,
  CloudUpload as AssignmentIcon,
  CheckCircle as CheckIcon,
  HourglassEmpty as HourglassIcon,
  AccessTime as PendingIcon,
  Send as ReadyToSendIcon,
  MarkEmailRead as EmailSentIcon,
  MarkEmailUnread as EmailFailedIcon,
  MarkunreadMailbox as ResponseReceivedIcon,
  AttachMoney as FinalCostIcon,
  Cancel as RejectedIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

interface StatusCounts {
  pending: number;
  readyToSend: number;
  emailFailed: number;
  emailSent: number;
  responseReceived: number;
  finalCost: number;
  rejected: number;
}

interface UserTaskBreakdown {
  name: string;
  assignedTasks: number;
  remainingTasks: number;
  completedTasks: number;
  pendingTasks?: number;
  assignedSites: number;
  remainingSites: number;
  completedSites: number;
  statusCounts: StatusCounts;
}

interface DashboardStats {
  totalUsers?: number;
  assignedTasks: number;
  completedTasks: number;
  pendingTasks: number;
  remainingTasks?: number;
  assignedSites?: number;
  remainingSites?: number;
  completedSites?: number;
  completedDataRows?: number;
  pendingDataRows?: number;
  totalDataRows?: number;
  pendingTasksTotalData?: number;
  completedTasksTotalData?: number;
  superAdminCompletedRows?: number;
  updatedDataRows?: number;
  reachedDataCount?: number;
  pendingNotReachedCount?: number;
  currentPendingTasks?: number;
  period?: string;
  statusCounts?: StatusCounts;
  userTaskBreakdown?: UserTaskBreakdown[];
}

type DashboardPeriod = 'today' | '7d' | 'all';

const emptyStatusCounts: StatusCounts = {
  pending: 0,
  readyToSend: 0,
  emailFailed: 0,
  emailSent: 0,
  responseReceived: 0,
  finalCost: 0,
  rejected: 0
};

const STATUS_CARD_DEFS: Array<{
  key: keyof StatusCounts;
  title: string;
  Icon: React.ElementType;
  color: string;
}> = [
  { key: 'pending', title: 'Pending', Icon: PendingIcon, color: '#ff9800' },
  { key: 'readyToSend', title: 'Ready to send', Icon: ReadyToSendIcon, color: '#0288d1' },
  { key: 'emailFailed', title: 'Email Failed', Icon: EmailFailedIcon, color: '#d32f2f' },
  { key: 'emailSent', title: 'Email Sent', Icon: EmailSentIcon, color: '#2e7d32' },
  { key: 'responseReceived', title: 'Response Received', Icon: ResponseReceivedIcon, color: '#1565c0' },
  { key: 'finalCost', title: 'Final Cost', Icon: FinalCostIcon, color: '#6a1b9a' },
  { key: 'rejected', title: 'Rejected', Icon: RejectedIcon, color: '#616161' }
];

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [period, setPeriod] = useState<DashboardPeriod>('all');
  const [stats, setStats] = useState<DashboardStats>({
    assignedTasks: 0,
    completedTasks: 0,
    pendingTasks: 0
  });
  const [loading, setLoading] = useState(true);

  const fetchDashboardStats = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { period }
      });

      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (err: any) {
      console.error('Error fetching dashboard stats:', err);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  const statusCounts = stats.statusCounts || emptyStatusCounts;
  const assignedSites = stats.assignedSites ?? stats.totalDataRows ?? 0;
  const remainingSites = stats.remainingSites ?? stats.pendingDataRows ?? 0;
  const completedSites = stats.completedSites ?? stats.completedDataRows ?? 0;

  const periodLabel =
    period === 'today' ? 'Today' : period === '7d' ? 'Last 7 days' : 'All time';

  const taskCards = [
    ...(user?.role === 'SUPER_ADMIN' && stats.totalUsers !== undefined
      ? [{
          title: 'Total Users',
          value: stats.totalUsers,
          subtitle: 'Active accounts',
          icon: <PeopleIcon />,
          color: '#1976d2'
        }]
      : []),
    {
      title: 'Assigned',
      value: assignedSites,
      subtitle: 'sites',
      icon: <AssignmentIcon />,
      color: '#ff9800'
    },
    {
      title: 'Remaining',
      value: remainingSites,
      subtitle: 'sites pending',
      icon: <HourglassIcon />,
      color: '#9c27b0'
    },
    {
      title: 'Completed',
      value: completedSites,
      subtitle: 'sites done',
      icon: <CheckIcon />,
      color: '#4caf50'
    }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ mb: 0.5 }}>
            Welcome back, {user?.firstName}!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {user?.role === 'SUPER_ADMIN'
              ? "Here's an overview of your guest blog validation system"
              : user?.role === 'CONTRIBUTOR'
                ? "Here's an overview of your uploaded tasks"
                : "Here's an overview of your assigned tasks"}
          </Typography>
        </Box>
        <ToggleButtonGroup
          value={period}
          exclusive
          onChange={(_, value: DashboardPeriod | null) => {
            if (value) setPeriod(value);
          }}
          size="small"
          color="primary"
        >
          <ToggleButton value="today">Today</ToggleButton>
          <ToggleButton value="7d">Last 7 days</ToggleButton>
          <ToggleButton value="all">All time</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
        Showing stats for: <strong>{periodLabel}</strong>
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
            Tasks overview
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {taskCards.map((stat) => (
              <Grid item xs={12} sm={6} md={3} key={stat.title}>
                <Card sx={{ height: '100%', minHeight: 120 }}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Box>
                        <Typography color="text.secondary" variant="body2" gutterBottom>
                          {stat.title}
                        </Typography>
                        <Typography variant="h4" fontWeight="bold">
                          {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          {stat.subtitle}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          backgroundColor: stat.color,
                          borderRadius: 2,
                          p: 1.5,
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {stat.icon}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
            Status counts (sites)
          </Typography>
          <Grid container spacing={2}>
            {STATUS_CARD_DEFS.map((def) => {
              const Icon = def.Icon;
              return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={def.key}>
                <Card sx={{ height: '100%', borderTop: `4px solid ${def.color}` }}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography color="text.secondary" variant="body2" gutterBottom>
                          {def.title}
                        </Typography>
                        <Typography variant="h4" fontWeight="bold" sx={{ color: def.color }}>
                          {statusCounts[def.key].toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          sites · {periodLabel}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          backgroundColor: def.color,
                          borderRadius: 2,
                          width: 48,
                          height: 48,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff'
                        }}
                      >
                        <Icon />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              );
            })}
          </Grid>
        </>
      )}

      {user?.role === 'SUPER_ADMIN' && !loading && stats.userTaskBreakdown && stats.userTaskBreakdown.length > 0 && (
        <Card sx={{ mt: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              User breakdown
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Tasks + site status counts per Super Admin and Admin ({periodLabel})
            </Typography>
            <Grid container spacing={2}>
              {stats.userTaskBreakdown.map((userStat) => {
                const counts = userStat.statusCounts || emptyStatusCounts;
                return (
                  <Grid item xs={12} md={6} key={userStat.name}>
                    <Box
                      sx={{
                        p: 2.5,
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                        height: '100%',
                        background: 'linear-gradient(180deg, #fafafa 0%, #fff 40%)'
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        {userStat.name}
                      </Typography>

                      <Grid container spacing={1} sx={{ mb: 2 }}>
                        {[
                          { label: 'Assigned', tasks: userStat.assignedTasks, sites: userStat.assignedSites, color: '#ff9800' },
                          { label: 'Remaining', tasks: userStat.remainingTasks ?? userStat.pendingTasks ?? 0, sites: userStat.remainingSites, color: '#9c27b0' },
                          { label: 'Completed', tasks: userStat.completedTasks, sites: userStat.completedSites, color: '#4caf50' }
                        ].map((item) => (
                          <Grid item xs={4} key={item.label}>
                            <Box
                              sx={{
                                p: 1,
                                borderRadius: 1.5,
                                bgcolor: `${item.color}14`,
                                border: `1px solid ${item.color}33`
                              }}
                            >
                              <Typography variant="caption" sx={{ color: item.color, fontWeight: 700 }}>
                                {item.label}
                              </Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {item.tasks} tasks
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {item.sites.toLocaleString()} sites
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>

                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                        Status counts
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {STATUS_CARD_DEFS.map((def) => {
                          const Icon = def.Icon;
                          return (
                          <Box
                            key={def.key}
                            sx={{
                              px: 1.25,
                              py: 0.75,
                              borderRadius: 1.5,
                              bgcolor: `${def.color}14`,
                              border: `1px solid ${def.color}40`,
                              minWidth: 110
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                              <Icon sx={{ fontSize: 16, color: def.color }} />
                              <Typography variant="caption" sx={{ color: def.color, fontWeight: 700 }}>
                                {def.title}
                              </Typography>
                            </Box>
                            <Typography variant="body2" fontWeight="bold">
                              {counts[def.key].toLocaleString()}
                            </Typography>
                          </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </CardContent>
        </Card>
      )}

      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Quick Actions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user?.role === 'SUPER_ADMIN'
              ? 'Upload a new CSV file to start processing guest blog submissions'
              : 'View and manage your assigned data processing tasks'}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Dashboard;
